var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Route wrapper for the direct (admin) edit of a published authority record.
 * AR-02 alternativní scénář A1 — bypasses the proposal workflow entirely.
 *
 * Loads the record via {@link useRecordDetail}, converts its grouped
 * {@code dataFields} back into flat {@code ProposalDataField[]} for the
 * generic {@code RecordForm}, and submits via {@link updateRecord}
 * (PUT /czna-authorities/records/{id}).
 *
 * The route is gated on the client only — backend authorization (capability
 * {@code czna-authorities.records.item.put}) is the source of truth.
 *
 * <b>CZNA-489 — where the form comes from.</b> Until this ticket the route read
 * the form straight out of {@code formConfigRegistry.getFormConfig()}, the
 * bundled TS registry. That registry is explicitly only a 5xx/network fallback
 * and knows PERSONAL, CORPORATE and MEETING; for every other type it returns
 * {@code null}, so the route answered with the calm "form not available yet"
 * banner — 133 532 active records (measured 2026-08-12), even though the
 * backend serves a compiled form config for all of their types. The route now
 * resolves the config the same way {@code ProposalForm} has since TASK-114:
 * {@link useFormConfig} = {@code GET /module-settings/effective/{TYPE}}. Since
 * CZNA-1292 there is no registry fallback behind it at all: a config that cannot
 * be fetched is an error on this route too, never the bundled template.
 *
 * That split is why the component is two components: {@link useFormConfig}
 * needs {@code record.type}, and React forbids calling a hook after the
 * record-loading early returns. The outer component loads the record, the inner
 * one owns the form.
 *
 * `VocabularyEdit` is a structural twin on a different route
 * (`/vocabularies/:id/edit`) and keeps its own `vocabularyFormConfig` — the
 * effective endpoint 404s for VOCABULARY_* types, verified 2026-08-12.
 */
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { Button, Loading, Layout, MessageBanner, } from '@folio/stripes/components';
import useRecordDetail from '../hooks/useRecordDetail';
import useFormConfig from '../hooks/useFormConfig';
import { describeFieldLocation, shownTabs } from '../components/ProposalForm/utils/locateField';
import RecordForm from '../components/ProposalForm/RecordForm';
import { WarningModal } from '../components/Validation/WarningModal';
import { matchTemplateShape } from '../components/ProposalForm/utils/templateShapeRouting';
import { proposalDataToGenericFormState } from '../components/ProposalForm/utils/proposalDataToFormState';
import { genericFormToProposalData } from '../components/ProposalForm/utils/genericFormToProposalData';
import { recordDataFieldsToProposalData } from '../components/ProposalForm/utils/recordDataFieldsToProposalData';
import { extractControlFields } from '../utils/marcControlFields';
import { fetchRecord, updateRecord } from '../api/recordService';
import { validateRecord } from '../api/validationService';
import { proposalDataToMarc } from '../utils/recordToMarc';
import { formatSaveError } from '../utils/formatSaveError';
import { isTimeoutError } from '../utils/errorKind';
import { saveOutcomeUnknownError, toSaveFailure, verificationFromVersions, } from '../utils/saveOutcomeUnknown';
import { recordDisplayId } from '../utils/recordDisplayId';
import RecordReviewActions from '../components/RecordReviewActions';
import { useBackLinkOffers } from '../components/BackLinkOffer/useBackLinkOffers';
/**
 * The edit form itself, mounted only once the record is loaded — which is what
 * lets {@link useFormConfig} be called unconditionally with `record.type`.
 *
 * `id` comes from the route rather than `record.id` so the cache keys written
 * here match the ones {@link useRecordDetail} read under.
 */
var RecordEditForm = function (_a) {
    var _b, _c, _d;
    var id = _a.id, record = _a.record;
    var intl = useIntl();
    var history = useHistory();
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _e = useBackLinkOffers(), offerBackLinks = _e.offerBackLinks, backLinkOfferModal = _e.backLinkOfferModal;
    // CZNA-489: the compiled EFC from the backend — and since CZNA-1292 nothing else.
    //
    // CZNA-1087 — and for a type with more than one SHAPE, the right one of those.
    // The first call resolves the type's default template and brings back its
    // `templateRouting`; `matchTemplateShape` reads the record against it. For every
    // type but CONSPECT_GROUP, and for the 598 of 624 conspect records that ARE the
    // default shape, the match is `undefined`, the second call has the identical query
    // key, and react-query serves it from the first one's cache — one request, as before.
    // The 26 předmětové kategorie cost a second request and get the form whose required
    // fields they can actually satisfy.
    //
    // Two hook calls rather than one hook that chains: the routing table is published ON
    // the EFC, so there is nothing to ask until the first answer is in, and making
    // `useFormConfig` fetch twice internally would hide that from every other caller.
    var defaultShapeConfig = useFormConfig(record.type).config;
    var shapeTemplateId = matchTemplateShape(defaultShapeConfig === null || defaultShapeConfig === void 0 ? void 0 : defaultShapeConfig.templateRouting, record.dataFields, defaultShapeConfig === null || defaultShapeConfig === void 0 ? void 0 : defaultShapeConfig.templateId);
    var _f = useFormConfig(record.type, shapeTemplateId), config = _f.config, isConfigLoading = _f.isLoading;
    // CZNA-221: validation preflight + WARNING-only modal (mirrors ProposalForm).
    // The duplicate-heading check surfaces here; the user confirms "Uložit přesto"
    // to resume the deferred save. BLOCKING errors short-circuit to RecordForm's
    // banner exactly as on the proposal path (the backend re-validates anyway).
    var _g = useState(null), validationResult = _g[0], setValidationResult = _g[1];
    var _h = useState(null), pendingSave = _h[0], setPendingSave = _h[1];
    /**
     * CZNA-764 — the save-failure channel for the write the WARNING modal resumes. That write
     * happens with no `onSave` call in flight, so RecordForm cannot catch it; this prop is how
     * it reaches the one save-error banner instead of an empty `catch`.
     */
    var _j = useState(null), deferredSaveError = _j[0], setDeferredSaveError = _j[1];
    /** CZNA-764 — true while the save the modal confirmed is being written. See {@link handleWarningConfirm}. */
    var _k = useState(false), savingDeferred = _k[0], setSavingDeferred = _k[1];
    /**
     * CZNA-1043 — same defect as `ProposalForm`'s `handleWarningConfirm`: `persist()`
     * below (resumed here, not through `onSave`) calls `history.push`, and
     * RecordForm's `history.block` guard — still registered from the render before
     * the write landed — silently swallowed that push. See the ref's own doc on
     * `RecordFormProps`.
     */
    var leaveWithoutAskingRef = useRef(null);
    /**
     * CZNA-773 — the version the next save is sent against, when it is no longer the one in
     * `record`. A 409 used to leave `record.optimisticLock` in place, so every further attempt
     * collided again, and the obvious cure — invalidating `['record', id]` — changes the
     * `RecordForm` key below and remounts it, throwing away the draft the cataloguer is trying
     * to save. The route therefore reads the current version itself, outside the query cache,
     * and keeps it here. `id` and `from` pin it to the record and props it was adopted over:
     * once the cache brings a newer `record` (and with it a fresh form), the props win again —
     * and so does another record, since this component is not remounted when the route's `id`
     * changes.
     */
    var adoptedLockRef = useRef(null);
    var currentLock = function () { return (adoptedLockRef.current
        && adoptedLockRef.current.id === id
        && adoptedLockRef.current.from === record.optimisticLock
        ? adoptedLockRef.current.lock
        : record.optimisticLock); };
    var detailUrl = "/czna-authorities/authorities/".concat(id);
    var handleCancel = function () {
        history.push(detailUrl);
    };
    if (isConfigLoading) {
        return React.createElement(Loading, null);
    }
    if (!config) {
        // CZNA-1292 — one explicit error with a retry for every reason the config
        // is missing (404, 403, 5xx, network, a 200 without `tabs`), the same state
        // `ProposalForm` shows. CZNA-489 split this into a calm "unsupported type"
        // warning for a 404 and a generic load error for the rest; with the registry
        // fallback gone a 404 reaches this branch for PERSONAL / CORPORATE / MEETING
        // too, where "no form for this type" is simply false, and the generic text
        // blamed the record, which did load. Saying the form config could not be
        // loaded is true for all of them, and never the calm banner a failure
        // could hide behind — the trap CZNA-489 was made of.
        return (React.createElement(Layout, { className: "padding-all-gutter", "data-testid": "record-edit-config-unavailable" },
            React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.configUnavailable", values: { type: React.createElement(FormattedMessage, { id: "ui-czna-authorities.recordType.".concat(record.type) }) } })),
            React.createElement(Button, { buttonStyle: "primary", onClick: function () { return window.location.reload(); } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.error.boundary.retry" }))));
    }
    // Convert grouped record_data → flat proposal_data shape, then to form state.
    var flatRecordData = recordDataFieldsToProposalData((_b = record.dataFields) !== null && _b !== void 0 ? _b : []);
    var initialFormData = proposalDataToGenericFormState(config, flatRecordData);
    // CZNA-504: control fields (001/003/005/008) survive the round-trip above as
    // rows, but not into the form: the FormConfig gives them no subfields to hold
    // a value, so `proposalDataToGenericFormState` has nowhere to put them. They
    // go to the MARC preview straight off the loaded record instead, which is the
    // only place that still has them.
    //
    // Note the identifier this forces into the open: the read-only 001 row in the
    // form was showing `authorityId` (`AUT-000247904`), while the record's actual
    // MARC 001 — the value the preview now renders — is `exportId`
    // (`xx0023870` on an Aleph-imported record). The two are equal only for
    // records we created ourselves, which is why the mismatch stayed invisible;
    // the corpus is 1.27 M imported ones. Both places show `exportId` now, so a
    // field labelled 001 carries what 001 actually holds.
    var previewControlFields = extractControlFields(record.dataFields);
    /**
     * CZNA-1625 — "Načíst znovu" under a save the server did not answer in time. The record is
     * read past the query cache (an invalidation would change RecordForm's key and remount it,
     * wiping the edits — the CZNA-773 trap) and its version compared with the one the save went
     * out on:
     *  - moved → the write landed: do what a successful save does — the detail, with its
     *    "Změny byly uloženy" toast. Its back-link offers went down with the lost response.
     *  - unchanged → nothing was written: the edits stay, and the version read is the one the
     *    next "Uložit" goes out on (the same takeover as after a 409).
     *  - unreadable → nobody knows yet; the banner stays and says so.
     */
    var verifyTimedOutSave = function (sentLock) { return __awaiter(void 0, void 0, void 0, function () {
        var fresh, _a, outcome;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, fetchRecord(ky, id)];
                case 1:
                    fresh = _b.sent();
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    return [2 /*return*/, 'unverified'];
                case 3:
                    outcome = verificationFromVersions(sentLock, fresh === null || fresh === void 0 ? void 0 : fresh.optimisticLock);
                    if (outcome === 'saved') {
                        queryClient.invalidateQueries(['record', id]);
                        queryClient.invalidateQueries(['czna-authorities-records']);
                        queryClient.invalidateQueries(['review-queue']);
                        history.push(detailUrl, { savedToast: true });
                    }
                    else if (outcome === 'notSaved') {
                        adoptedLockRef.current = { id: id, from: record.optimisticLock, lock: fresh.optimisticLock };
                    }
                    return [2 /*return*/, outcome];
            }
        });
    }); };
    // Inner write — the actual PUT. Wrapped by the preflight in {@link saveWithPreflight}.
    //
    // There is no edit-and-approve variant. TASK-084 threaded an `approveOnSave` flag through
    // here and drew a second footer button for a PENDING_REVIEW record, but the backend could
    // not serve it for any input (400 for a non-vocabulary record, 422 for a vocabulary one —
    // this endpoint has no heading rule for vocabulary types). Removed 2026-08-19; approval is
    // the review queue's own "Schválit" button, which uses PATCH /records/{id}/review.
    //
    // CZNA-1508 — `onWritten` runs the moment the PUT has landed, before the back-link offer.
    // The offer can stay up for as long as the cataloguer takes over it, and everything that
    // says "saving" (the WARNING modal, RecordForm's button) has to stop saying it here — the
    // record IS saved by then. Not called on a failed PUT.
    var persist = function (recordData, onWritten) { return __awaiter(void 0, void 0, void 0, function () {
        var sentLock, saved, error_1, status_1, fresh, _a, _b;
        var _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    sentLock = currentLock();
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 4, , 11]);
                    return [4 /*yield*/, updateRecord(ky, id, {
                            type: record.type,
                            optimisticLock: sentLock,
                            record_data: recordData, // eslint-disable-line camelcase
                        })];
                case 2:
                    saved = _d.sent();
                    onWritten === null || onWritten === void 0 ? void 0 : onWritten();
                    // CZNA-1409 — the edit has landed; the back-link offers are answered before we leave.
                    return [4 /*yield*/, offerBackLinks(saved === null || saved === void 0 ? void 0 : saved.backLinkOffers)];
                case 3:
                    // CZNA-1409 — the edit has landed; the back-link offers are answered before we leave.
                    _d.sent();
                    queryClient.invalidateQueries(['record', id]);
                    queryClient.invalidateQueries(['czna-authorities-records']);
                    queryClient.invalidateQueries(['review-queue']);
                    // One-shot success toast: AuthoritiesList reads location.state on mount,
                    // emits its Callout once, then clears the flag via history.replace.
                    history.push(detailUrl, { savedToast: true });
                    return [3 /*break*/, 11];
                case 4:
                    error_1 = _d.sent();
                    status_1 = (_c = error_1 === null || error_1 === void 0 ? void 0 : error_1.response) === null || _c === void 0 ? void 0 : _c.status;
                    if (!(status_1 === 409)) return [3 /*break*/, 9];
                    fresh = null;
                    _d.label = 5;
                case 5:
                    _d.trys.push([5, 7, , 8]);
                    return [4 /*yield*/, fetchRecord(ky, id)];
                case 6:
                    fresh = _d.sent();
                    return [3 /*break*/, 8];
                case 7:
                    _a = _d.sent();
                    fresh = null;
                    return [3 /*break*/, 8];
                case 8:
                    // An unchanged version means this 409 was not a lost race (the backend also answers
                    // 409 for an IllegalStateException), so there is nothing to take over and the
                    // general message stays. A failed read leaves the old one too: it asks for a reload,
                    // which is then the only thing that can help.
                    if (fresh && typeof fresh.optimisticLock === 'number' && fresh.optimisticLock !== sentLock) {
                        adoptedLockRef.current = { id: id, from: record.optimisticLock, lock: fresh.optimisticLock };
                        throw new Error(intl.formatMessage({ id: 'ui-czna-authorities.record.edit.optimisticLockConflictDraftKept' }));
                    }
                    _d.label = 9;
                case 9:
                    // CZNA-1625: no answer in time — the PUT may have landed. Say so, and offer to find out.
                    if (isTimeoutError(error_1)) {
                        throw saveOutcomeUnknownError(intl.formatMessage({ id: 'ui-czna-authorities.record.edit.timeout' }), { kind: 'verify', verify: function () { return verifyTimedOutSave(sentLock); } });
                    }
                    _b = Error.bind;
                    return [4 /*yield*/, formatSaveError(error_1, intl)];
                case 10: throw new (_b.apply(Error, [void 0, _d.sent()]))();
                case 11: return [2 /*return*/];
            }
        });
    }); };
    // CZNA-221: run the validation preflight, then block (errors), defer + modal
    // (warnings), or save (clean). Operation UPDATE_DRAFT matches the backend's
    // direct-edit re-validation; excludeAuthorityId stops the record flagging
    // itself when the heading is unchanged.
    var saveWithPreflight = function (formData, onWritten) { return __awaiter(void 0, void 0, void 0, function () {
        var recordData, result, _a;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    // CZNA-764: a fresh attempt must not run under the previous attempt's message.
                    setDeferredSaveError(null);
                    recordData = genericFormToProposalData(config, formData);
                    result = null;
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, validateRecord(ky, {
                            record: proposalDataToMarc(recordData),
                            recordType: record.type,
                            operation: 'UPDATE_DRAFT',
                            excludeAuthorityId: (_b = record.authorityId) !== null && _b !== void 0 ? _b : undefined,
                            // CZNA-1506 — judge the record the save stores. The direct-edit save
                            // (`applyDirectEdit`) stamps a picked 5XX link's $7 from the FK on its $a, and
                            // the tracing's $w r; without this ALEPH_6116 warned about a $7 the stored record
                            // has. Deliberately not `applyDerivedControlFields`: that one also derives an 008,
                            // which the direct-edit save does not, so the rules would see a field it never writes.
                            applyDerivedSubfields: true,
                            // CZNA-1619 — the rows carry every link the pickers made, so a 368/372/374 value
                            // without one is unlinked and the check warns about it.
                            carriesLinks: true,
                        })];
                case 2:
                    result = _c.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = _c.sent();
                    // Preflight failed — fall through to save; the backend re-validates and
                    // rejects with 422 if needed (we don't let a flaky endpoint block edits).
                    result = null;
                    return [3 /*break*/, 4];
                case 4:
                    setValidationResult(result);
                    if (result && result.errorCount > 0) {
                        throw new Error(intl.formatMessage({ id: 'ui-czna-authorities.validation.banner.errorCount' }, { count: result.errorCount }));
                    }
                    if (result && result.warningCount > 0) {
                        // Park the save; the modal's "Uložit přesto" resumes it. Don't throw —
                        // RecordForm must not render its save-error banner; the modal takes over.
                        setPendingSave(function () { return function (onWritten) { return persist(recordData, onWritten); }; });
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, persist(recordData, onWritten)];
                case 5:
                    _c.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    // CZNA-1508 — `written` tells RecordForm's button to stop showing "Ukládá se…" once the
    // PUT lands (see `persist`). A save parked behind the WARNING modal never calls it: that
    // `onSave` resolves at the parking, and the modal shows its own progress.
    var handleSave = function (formData, signals) { return (saveWithPreflight(formData, signals === null || signals === void 0 ? void 0 : signals.written)); };
    var handleWarningCancel = function () { return setPendingSave(null); };
    var handleWarningConfirm = function () { return __awaiter(void 0, void 0, void 0, function () {
        var save, written, run, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    save = pendingSave;
                    if (!save)
                        return [2 /*return*/];
                    setDeferredSaveError(null);
                    // CZNA-764: hold the modal up, both buttons inert, until the write settles — closing it
                    // first left the PUT running behind a form that looked untouched, with "Uložit" still
                    // live, which is an invitation to click again on the same `optimisticLock`.
                    setSavingDeferred(true);
                    written = function () {
                        setPendingSave(null);
                        setSavingDeferred(false);
                    };
                    run = function () { return save(written); };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    // CZNA-1043 — see `leaveWithoutAskingRef`'s own doc above.
                    return [4 /*yield*/, (leaveWithoutAskingRef.current ? leaveWithoutAskingRef.current(run) : run())];
                case 2:
                    // CZNA-1043 — see `leaveWithoutAskingRef`'s own doc above.
                    _a.sent();
                    setPendingSave(null);
                    return [3 /*break*/, 5];
                case 3:
                    error_2 = _a.sent();
                    setPendingSave(null);
                    // CZNA-764: this was an empty `catch` — "persist() throws translated messages;
                    // swallow so the modal just closes". Closing was the whole problem: nothing else
                    // happened, so a 409 left a form identical to the one before the click and the
                    // cataloguer concluded the edit had not been saved. `formatSaveError` has already
                    // produced the specific sentence (reload for 409, ask for the right on 403); hand
                    // it to the banner.
                    // CZNA-1625: a timeout reaches the banner whole, with its "Načíst znovu".
                    setDeferredSaveError(toSaveFailure(error_2, intl.formatMessage({ id: 'ui-czna-authorities.record.edit.saveError' })));
                    return [3 /*break*/, 5];
                case 4:
                    setSavingDeferred(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement(React.Fragment, null,
        React.createElement(RecordForm, { key: "".concat(id, "-").concat((_c = record.optimisticLock) !== null && _c !== void 0 ? _c : 0), config: config, recordType: record.type, initialData: initialFormData, isEdit: true, mode: "update", targetRecordAuthorityIdDisplay: recordDisplayId(record), excludeAuthorityId: record.authorityId, previewControlFields: previewControlFields, metadata: {
                createdBy: record.createdBy,
                createdAt: record.createdAt,
                updatedBy: record.updatedBy,
                updatedAt: record.updatedAt,
            }, onSave: handleSave, saveError: deferredSaveError, onCancel: handleCancel, leaveWithoutAskingRef: leaveWithoutAskingRef, extraFooterActions: function (_a) {
                var isDirty = _a.isDirty;
                return (React.createElement(RecordReviewActions, { record: record, disabled: isDirty, onActionComplete: function () { return history.push(detailUrl); } }));
            }, 
            // The leave guard's default text is the proposal editor's: it warns the
            // cataloguer her "návrh" will not even reach Koncepty and tells her to
            // save it as a draft instead. This route is the DIRECT edit of an
            // already-published record (AR-02 A1) — it bypasses the proposal
            // workflow, so there is no návrh and no Koncepty here and that advice
            // cannot be followed. Same override, same reason, as `VocabularyEdit`.
            leaveModalHeadingKey: "ui-czna-authorities.record.edit.leaveModal.heading", leaveModalMessageKey: "ui-czna-authorities.record.edit.leaveModal.message", paneId: "recordEditPane" }),
        React.createElement(WarningModal, { open: pendingSave !== null, confirmInFlight: savingDeferred, warnings: (_d = validationResult === null || validationResult === void 0 ? void 0 : validationResult.warnings) !== null && _d !== void 0 ? _d : [], describeLocation: describeFieldLocation(shownTabs(config, undefined), intl), onCancel: handleWarningCancel, onConfirm: handleWarningConfirm }),
        backLinkOfferModal));
};
/** Route entry point — loads the record, then hands it to the form. */
var RecordEdit = function () {
    var id = useParams().id;
    var _a = useRecordDetail(id), record = _a.record, isLoading = _a.isLoading, isError = _a.isError;
    if (isError) {
        return (React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.loadError" }))));
    }
    if (isLoading || !record) {
        return React.createElement(Loading, null);
    }
    return React.createElement(RecordEditForm, { id: id, record: record });
};
export default RecordEdit;
